import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import { ArrowRight, Clock, Users } from "lucide-react";

const LiveExamples = () => {
  return (
    <section
      data-testid="live-examples-section"
      className="dark-section relative overflow-hidden py-24 lg:py-32"
    >
      <div className="absolute inset-0 bg-dot-grid opacity-50" />
      <div className="aurora" />
      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-7">
            <SectionLabel index="06" label="Live demo examples" dark />
            <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[58px] tracking-tight leading-[1.02]">
              From a 30-minute case<br />
              <span className="text-white/55">to a 5-minute decision.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:self-end">
            <p className="text-white/65 text-[17px] leading-relaxed">
              Two illustrative scenarios. Both replayable. Both cited. Both
              advisory — the officer always decides.
            </p>
          </div>
        </div>

        {/* Editorial banner */}
        <EditorialBanner
          gradient="violet-cyan"
          image="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=2400&q=80"
          kicker="Intelligence in action"
          headline="A whole layer of context, on top of every alert."
          chips={[
            { c: "#00e5ff", t: "Entity link" },
            { c: "#7c3aed", t: "Cohort context" },
            { c: "#f59e0b", t: "Rationale" },
          ]}
          testId="liveexamples-banner"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {/* Example 1 — Entity Resolution */}
          <div
            className="bg-[#0d0e16] p-8 lg:p-10"
            data-testid="example-entity-resolution"
          >
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-4 h-4 text-[#00e5ff]" />
              <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#00e5ff]">
                Example A — Missed risk surfaced
              </p>
            </div>
            <p className="font-display font-bold text-white text-[26px] lg:text-[30px] tracking-tight leading-tight">
              The risk no single silo could see — now visible.
            </p>

            <div className="mt-8 space-y-3">
              {[
                { l: "Source A — Retail customer", v: "Identity signals" },
                { l: "Source B — SME director", v: "Identity signals" },
                { l: "Source C — Wallet user", v: "Device & account" },
                { l: "Source D — Counterparty", v: "Shared attributes" },
              ].map((r) => (
                <div
                  key={r.l}
                  className="flex items-center justify-between border-b border-white/10 pb-3"
                >
                  <span className="text-white/80 text-[14px]">{r.l}</span>
                  <span className="font-mono text-[11px] text-white/50 tracking-[0.08em]">{r.v}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-end justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                  Confidence
                </p>
                <p className="font-display font-bold text-white text-[44px] tracking-tight tabular leading-none">
                  High
                </p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#ff3b30]">
                  Trust band
                </p>
                <p className="font-display font-bold text-[#ff3b30] text-[34px] tracking-tight tabular leading-none">
                  Critical
                </p>
              </div>
            </div>
          </div>

          {/* Example 2 — FPRE & FFPRE */}
          <div
            className="bg-[#0d0e16] p-8 lg:p-10"
            data-testid="example-ffpre"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-4 h-4 text-[#00e5ff]" />
              <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#00e5ff]">
                Example B — FPRE (AML) &amp; FFPRE (Fraud)
              </p>
            </div>
            <p className="font-display font-bold text-white text-[26px] lg:text-[30px] tracking-tight leading-tight">
              Cutting alert handling — AML and Fraud — from ~30 min to ~5 min per case.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              <div className="bg-[#13141c] p-5">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45">
                  Without Sentinel
                </p>
                <p className="font-display font-bold text-white text-[36px] tabular tracking-tight leading-none mt-2">
                  ~30<span className="text-white/45 text-[18px] ml-1">min</span>
                </p>
                <p className="text-white/55 text-[12px] mt-2">Per case · manual review</p>
              </div>
              <div className="bg-[#13141c] p-5">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00e5ff]">
                  With Sentinel
                </p>
                <p className="font-display font-bold text-[#00e5ff] text-[36px] tabular tracking-tight leading-none mt-2">
                  ~5<span className="text-[#00e5ff]/60 text-[18px] ml-1">min</span>
                </p>
                <p className="text-white/55 text-[12px] mt-2">Full rationale below</p>
              </div>
            </div>

            <div className="mt-6 border border-white/10 p-5">
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/45 mb-3">
                Structured rationale &middot; FPRE / FFPRE
              </p>
              <ul className="space-y-2 text-white/75 text-[13.5px]">
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-3.5 h-3.5 mt-1 text-[#00e5ff]" />
                  <span><span className="font-mono text-[#00e5ff]">FFPRE</span> &middot; Cohort context suppresses repeat false positives</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-3.5 h-3.5 mt-1 text-[#00e5ff]" />
                  <span><span className="font-mono text-[#00e5ff]">FPRE</span> &middot; Screening hit triaged with structured evidence</span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-3.5 h-3.5 mt-1 text-[#00e5ff]" />
                  Cross-source history checked — clean
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="w-3.5 h-3.5 mt-1 text-[#00e5ff]" />
                  Cited to AMLA &amp; EBA RTS
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveExamples;
